import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Youtube, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { waLink } from "@/lib/whatsapp";

const links = [
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { settings } = useSettings();
  const { user } = useAuth();
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const brand = settings?.brand?.name || "NewStarAds";
  const wa = settings?.whatsapp || {};

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-slate-200" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 font-display font-extrabold text-lg tracking-tight">
          <span className="grid place-items-center w-8 h-8 rounded-lg bg-primary text-white">
            <Youtube className="w-5 h-5" strokeWidth={2} />
          </span>
          <span>{brand}</span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="px-3.5 py-2 text-sm font-medium text-slate-600 hover:text-primary rounded-lg hover:bg-slate-100 transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-2">
          {user && user.role === "admin" && (
            <Link to="/admin"><Button variant="ghost" size="sm" data-testid="nav-admin">Admin</Button></Link>
          )}
          {user && user.role === "client" && (
            <Link to="/dashboard"><Button variant="ghost" size="sm" data-testid="nav-dashboard">Dashboard</Button></Link>
          )}
          {!user && (
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-login">Login</Button></Link>
          )}
          <Link to="/consultation">
            <Button size="sm" className="rounded-full px-5" data-testid="nav-cta">
              Free Consultation
            </Button>
          </Link>
        </div>

        <button
          className="lg:hidden p-2 -mr-2 text-slate-700"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-white border-b border-slate-200 px-5 py-4 space-y-1" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="block px-3 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">
              {l.label}
            </Link>
          ))}
          <div className="pt-3 flex flex-col gap-2">
            {user?.role === "admin" && <Link to="/admin"><Button variant="outline" className="w-full">Admin</Button></Link>}
            {user?.role === "client" && <Link to="/dashboard"><Button variant="outline" className="w-full">Dashboard</Button></Link>}
            {!user && <Link to="/login"><Button variant="outline" className="w-full">Login</Button></Link>}
            <Link to="/consultation"><Button className="w-full rounded-full">Free Consultation</Button></Link>
            <a href={waLink(wa.number, wa.default_message)} target="_blank" rel="noreferrer">
              <Button variant="outline" className="w-full border-green-500 text-green-600">Chat on WhatsApp</Button>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
